/**
 * Two-level industry category hierarchy mapping NACE 2-digit divisions to
 * categories and subcategories.
 *
 * Data is loaded from industry-categories.json — the single source of truth,
 * mirrored in the backend database table `industry_category` (migration 219).
 */

import categoriesData from './industry-categories.json';

interface CategoryMeta {
  label_en: string;
  label_de: string;
  divisions: number[];
  nace_prefixes?: string[];
}

interface SubcategoryMeta extends CategoryMeta {
  parent: string;
}

const CATEGORIES: Record<string, CategoryMeta> = categoriesData.categories as Record<string, CategoryMeta>;
const SUBCATEGORIES: Record<string, SubcategoryMeta> = categoriesData.subcategories as Record<string, SubcategoryMeta>;

export const INDUSTRY_CATEGORIES: Record<string, { en: string; de: string }> = Object.fromEntries(
  Object.entries(CATEGORIES).map(([key, meta]) => [key, { en: meta.label_en, de: meta.label_de }]),
);

/** Reverse mapping: category key → NACE 2-digit divisions. */
export const CATEGORY_TO_DIVISIONS: Record<string, number[]> = Object.fromEntries(
  Object.entries(CATEGORIES).map(([key, meta]) => [key, meta.divisions]),
);

/** Division → category key, derived once at module load. */
const DIVISION_TO_CATEGORY: Record<number, string> = {};
for (const [key, meta] of Object.entries(CATEGORIES)) {
  for (const div of meta.divisions) DIVISION_TO_CATEGORY[div] = key;
}

/** Division → subcategory key (most specific match). */
const DIVISION_TO_SUBCATEGORY: Record<number, string> = {};
for (const [key, meta] of Object.entries(SUBCATEGORIES)) {
  for (const div of meta.divisions) DIVISION_TO_SUBCATEGORY[div] = key;
}

/** NACE prefix (e.g. "64.21") → category key. Specific matches beat division matches. */
const PREFIX_TO_CATEGORY: Record<string, string> = {};
for (const [key, meta] of Object.entries(CATEGORIES)) {
  for (const prefix of meta.nace_prefixes ?? []) PREFIX_TO_CATEGORY[prefix] = key;
}
const PREFIX_TO_SUBCATEGORY: Record<string, string> = {};
for (const [key, meta] of Object.entries(SUBCATEGORIES)) {
  for (const prefix of (meta as CategoryMeta).nace_prefixes ?? []) PREFIX_TO_SUBCATEGORY[prefix] = key;
}

/** Extract the 2-digit NACE division from a code string. */
function extractDivision(naceCode: string): number | null {
  const div = parseInt(naceCode.replace('.', '').slice(0, 2), 10);
  return isNaN(div) ? null : div;
}

/**
 * Extract the level-4 prefix (e.g. "64.21") from a NACE code string.
 * Handles "64.21.0", "64.21", "6421" formats.
 */
function extractPrefix(naceCode: string): string | null {
  // Dotted format: take first two dot-separated groups
  if (naceCode.includes('.')) {
    const parts = naceCode.split('.');
    if (parts.length >= 2) return `${parts[0]}.${parts[1]}`;
  }
  // Plain digits: first 2 digits + "." + next 2 digits
  const digits = naceCode.replace(/\D/g, '');
  if (digits.length >= 4) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}`;
  return null;
}

/** Map a NACE code (e.g. "62.01" or "62") to a high-level category key. */
export function naceToCategory(naceCode: string | null | undefined): string | null {
  if (!naceCode) return null;
  // Priority 1: specific prefix match
  const prefix = extractPrefix(naceCode);
  if (prefix && PREFIX_TO_CATEGORY[prefix]) return PREFIX_TO_CATEGORY[prefix];
  // Priority 2: division match
  const div = extractDivision(naceCode);
  if (div === null) return null;
  return DIVISION_TO_CATEGORY[div] ?? null;
}

/** Map a NACE code to its subcategory key (or null if the category has no subcategories). */
export function naceToSubcategory(naceCode: string | null | undefined): string | null {
  if (!naceCode) return null;
  const prefix = extractPrefix(naceCode);
  // If prefix matches a subcategory, use it
  if (prefix && PREFIX_TO_SUBCATEGORY[prefix]) return PREFIX_TO_SUBCATEGORY[prefix];
  // If prefix matched a top-level category, there's no subcategory
  if (prefix && PREFIX_TO_CATEGORY[prefix]) return null;
  // Fallback: division match
  const div = extractDivision(naceCode);
  if (div === null) return null;
  return DIVISION_TO_SUBCATEGORY[div] ?? null;
}

/** Get the parent category key for a subcategory. */
export function getSubcategoryParent(subKey: string): string | null {
  return SUBCATEGORIES[subKey]?.parent ?? null;
}

export function getCategoryLabel(key: string, lang: string): string {
  const cat = CATEGORIES[key];
  if (cat) return lang === 'de' ? cat.label_de : cat.label_en;
  const sub = SUBCATEGORIES[key];
  if (sub) return lang === 'de' ? sub.label_de : sub.label_en;
  return key;
}

/** All category keys sorted by their localized label. */
export function allIndustryCategoriesSorted(lang: string): { key: string; label: string }[] {
  return Object.keys(INDUSTRY_CATEGORIES)
    .map((key) => ({ key, label: getCategoryLabel(key, lang) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// Slug ↔ key mapping for SEO-friendly category URLs
// ---------------------------------------------------------------------------

/** Slugify a label for use in URLs. */
function slugifyLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Locale-aware slug for a category or subcategory key.
 * e.g. categorySlug("energy", "de") → "energie-versorgung"
 *      categorySlug("energy", "en") → "energy-utilities"
 */
export function categorySlug(key: string, lang: string): string {
  return slugifyLabel(getCategoryLabel(key, lang));
}

/** Reverse lookup: localized slug (any locale) → category key. */
const SLUG_TO_CATEGORY: Record<string, string> = {};
for (const key of Object.keys(CATEGORIES)) {
  SLUG_TO_CATEGORY[slugifyLabel(CATEGORIES[key].label_de)] = key;
  SLUG_TO_CATEGORY[slugifyLabel(CATEGORIES[key].label_en)] = key;
}

/** Reverse lookup: localized slug (any locale) → subcategory key. */
const SLUG_TO_SUBCATEGORY: Record<string, string> = {};
for (const key of Object.keys(SUBCATEGORIES)) {
  SLUG_TO_SUBCATEGORY[slugifyLabel(SUBCATEGORIES[key].label_de)] = key;
  SLUG_TO_SUBCATEGORY[slugifyLabel(SUBCATEGORIES[key].label_en)] = key;
}

/** Resolve a URL slug to a top-level category key (checks both locales). */
export function categoryKeyFromSlug(slug: string): string | null {
  return SLUG_TO_CATEGORY[slug] ?? null;
}

/** Resolve a URL slug to a subcategory key (checks both locales). */
export function subcategoryKeyFromSlug(slug: string): string | null {
  return SLUG_TO_SUBCATEGORY[slug] ?? null;
}

/**
 * Re-slug an industry category/subcategory URL segment for a target locale.
 * If the slug matches a known category or subcategory (in any locale),
 * returns the slug in the target locale. Otherwise returns it unchanged.
 */
export function relocalizeIndustrySlug(slug: string, targetLang: string): string {
  const catKey = SLUG_TO_CATEGORY[slug];
  if (catKey) return slugifyLabel(getCategoryLabel(catKey, targetLang));
  const subKey = SLUG_TO_SUBCATEGORY[slug];
  if (subKey) return slugifyLabel(getCategoryLabel(subKey, targetLang));
  return slug;
}

/** Check whether a category key is a top-level category (not a sub). */
export function isTopLevelCategory(key: string): boolean {
  return key in CATEGORIES;
}

/** Check whether a category key has subcategories. */
export function categoryHasChildren(key: string): boolean {
  return Object.values(SUBCATEGORIES).some((s) => s.parent === key);
}
